import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/AppLayout";
import { ThemeProvider } from "@/components/theme-provider";
import { PushSubscriptionManager } from "@/components/PushSubscriptionManager";
import { ModuleLoadBoundary } from "@/components/ModuleLoadBoundary";
import { recoverBodyScrollIfStuck } from "@/lib/bodyScrollLock";
import { startScrollMonitor } from "@/utils/scrollMonitor";

// Lazy-loaded pages — only downloaded when navigated to
const Login = lazy(() => import("@/pages/Login"));
const Agenda = lazy(() => import("@/pages/Agenda"));
const Pacientes = lazy(() => import("@/pages/Pacientes"));
const Ajuda = lazy(() => import("@/pages/Ajuda"));
const Profissionais = lazy(() => import("@/pages/Profissionais"));
const Financeiro = lazy(() => import("@/pages/Financeiro"));
const Prontuario = lazy(() => import("@/pages/Prontuario"));
const ProntuarioHistorico = lazy(() => import("@/pages/ProntuarioHistorico"));
const Estoque = lazy(() => import("@/pages/Estoque"));
const Chat = lazy(() => import("@/pages/Chat"));
const Servicos = lazy(() => import("@/pages/Servicos"));
const TabelaPrecos = lazy(() => import("@/pages/TabelaPrecos"));
const Configuracoes = lazy(() => import("@/pages/Configuracoes"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Registro = lazy(() => import("@/pages/Registro"));
const Laudos = lazy(() => import("@/pages/Laudos"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Cadastro = lazy(() => import("@/pages/Cadastro"));
const ConfirmarConsulta = lazy(() => import("@/pages/ConfirmarConsulta"));
const Receituario = lazy(() => import("@/pages/Receituario"));
const PoliticaPrivacidadePublica = lazy(() => import("@/pages/PoliticaPrivacidadePublica"));
const TermosServico = lazy(() => import("@/pages/TermosServico"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 min — avoid redundant refetches
      gcTime: 1000 * 60 * 15,   // 15 min cache retention
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <span className="text-sm">Carregando...</span>
      </div>
    </div>
  );
}

function AuthStateNavigator() {
  const navigate = useNavigate();

  useEffect(() => {
    recoverBodyScrollIfStuck();
    const stopScrollMonitor = startScrollMonitor();

    const handleVisibilityOrPageRestore = () => {
      recoverBodyScrollIfStuck();
    };

    const periodicRecoveryId = window.setInterval(() => {
      recoverBodyScrollIfStuck();
    }, 1500);

    window.addEventListener("pageshow", handleVisibilityOrPageRestore);
    document.addEventListener("visibilitychange", handleVisibilityOrPageRestore);
    window.addEventListener("focus", handleVisibilityOrPageRestore);

    return () => {
      stopScrollMonitor();
      window.clearInterval(periodicRecoveryId);
      window.removeEventListener("pageshow", handleVisibilityOrPageRestore);
      document.removeEventListener("visibilitychange", handleVisibilityOrPageRestore);
      window.removeEventListener("focus", handleVisibilityOrPageRestore);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hasRecoveryInHash = window.location.hash.includes("type=recovery");
    const search = new URLSearchParams(window.location.search);
    const hasRecoveryInQuery = search.get("type") === "recovery";

    if (!hasRecoveryInHash && !hasRecoveryInQuery) return;
    if (window.location.pathname === "/reset-password") return;

    const queryString = window.location.search || "";
    const hashString = window.location.hash || "";
    navigate(`/reset-password${queryString}${hashString}`, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        const hash = typeof window !== "undefined" ? window.location.hash : "";
        navigate(`/reset-password${hash}`, { replace: true });
      }

      if (event === "SIGNED_OUT") {
        navigate("/login", { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout><PushSubscriptionManager />{children}</AppLayout>;
}

function MasterRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isMaster } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isMaster) return <Navigate to="/" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isPasswordRecovery } = useAuth();
  if (loading) return <PageLoader />;

  // Detect recovery flow from URL before PASSWORD_RECOVERY event fires
  const isRecoveryUrl =
    window.location.pathname === "/reset-password" ||
    window.location.pathname === "/recovery" ||
    window.location.hash.includes("type=recovery") ||
    window.location.search.includes("type=recovery");

  if (user && !isPasswordRecovery && !isRecoveryUrl) return <Navigate to="/" replace />;
  return <main id="main-content">{children}</main>;
}

function PublicMainRoute({ children }: { children: React.ReactNode }) {
  return <main id="main-content">{children}</main>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" storageKey="nitfisio-theme" themes={["light", "dark"]}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <AuthStateNavigator />
            <ModuleLoadBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
                  <Route path="/recovery" element={<AuthRoute><ResetPassword /></AuthRoute>} />
                  <Route path="/reset-password" element={<AuthRoute><ResetPassword /></AuthRoute>} />
                  <Route path="/cadastro" element={<AuthRoute><Cadastro /></AuthRoute>} />
                  <Route path="/" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
                  <Route path="/agendamento" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
                  <Route path="/pacientes" element={<ProtectedRoute><Pacientes /></ProtectedRoute>} />
                  <Route path="/profissionais" element={<MasterRoute><Profissionais /></MasterRoute>} />
                  <Route path="/financeiro" element={<MasterRoute><Financeiro /></MasterRoute>} />
                  <Route path="/prontuario" element={<ProtectedRoute><ProntuarioHistorico /></ProtectedRoute>} />
                  <Route path="/prontuario/:id" element={<ProtectedRoute><Prontuario /></ProtectedRoute>} />
                  <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
                  <Route path="/estoque" element={<MasterRoute><Estoque /></MasterRoute>} />
                  <Route path="/servicos" element={<MasterRoute><Servicos /></MasterRoute>} />
                  <Route path="/tabela" element={<MasterRoute><TabelaPrecos /></MasterRoute>} />
                  <Route path="/ajuda" element={<ProtectedRoute><Ajuda /></ProtectedRoute>} />
                  <Route path="/configuracoes" element={<MasterRoute><Configuracoes /></MasterRoute>} />
                  <Route path="/dashboard" element={<MasterRoute><Dashboard /></MasterRoute>} />
                  <Route path="/registro" element={<MasterRoute><Registro /></MasterRoute>} />
                  <Route path="/laudos" element={<ProtectedRoute><Laudos /></ProtectedRoute>} />
                  <Route path="/receituario" element={<ProtectedRoute><Receituario /></ProtectedRoute>} />
                  <Route path="/confirmar" element={<PublicMainRoute><ConfirmarConsulta /></PublicMainRoute>} />
                  <Route path="/politica-de-privacidade-publica" element={<PublicMainRoute><PoliticaPrivacidadePublica /></PublicMainRoute>} />
                  <Route path="/termos-de-servico" element={<PublicMainRoute><TermosServico /></PublicMainRoute>} />
                  <Route path="*" element={<PublicMainRoute><NotFound /></PublicMainRoute>} />
                </Routes>
              </Suspense>
            </ModuleLoadBoundary>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
